import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api';
import { nextQuarterHourValue, zonedDateTimeToIso } from '../time';
import type { Campaign, CampaignRole, Me, Run } from '../types';
import {
  EmptyState,
  ErrorNotice,
  Loading,
  Section,
  StatusPill,
  formString,
  formatNzDateTime,
  stringValue,
} from '../components/Common';

function RunCard({ run, me, role }: { run: Run; me: Me; role: CampaignRole }) {
  const queryClient = useQueryClient();
  const [showClose, setShowClose] = useState(false);
  const returnableBalance = Math.max(0, run.stockBalance);
  const [returnedQuantity, setReturnedQuantity] = useState(returnableBalance);
  useEffect(() => {
    if (showClose) setReturnedQuantity(returnableBalance);
  }, [returnableBalance, showClose]);
  const joined = run.participantIds.includes(me.user.id);
  const canManage = role === 'admin' || role === 'coordinator' || run.leader.id === me.user.id;
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['runs'] }),
      queryClient.invalidateQueries({ queryKey: ['me'] }),
      queryClient.invalidateQueries({ queryKey: ['inventory'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };
  const join = useMutation({
    mutationFn: () => apiClient.joinRun(run.id),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (status: string) => apiClient.updateRun(run.id, { status }),
    onSuccess: invalidate,
  });
  const close = useMutation({
    mutationFn: (finalStatus: 'completed' | 'cancelled') =>
      apiClient.closeRun(run.id, returnedQuantity, undefined, finalStatus),
    onSuccess: async () => {
      setShowClose(false);
      await invalidate();
    },
  });
  return (
    <article className="run-card">
      <div className="run-card-heading">
        <div>
          <p className="eyebrow">{run.wave.name}</p>
          <h3>{run.name}</h3>
        </div>
        <StatusPill
          tone={
            run.status === 'active'
              ? 'success'
              : run.status === 'paused'
                ? 'warning'
                : run.status === 'completed'
                  ? 'neutral'
                  : 'info'
          }
        >
          {run.status}
        </StatusPill>
      </div>
      <dl className="run-facts">
        <div>
          <dt>Starts</dt>
          <dd>{formatNzDateTime(run.startsAt)}</dd>
        </div>
        <div>
          <dt>Focus</dt>
          <dd>
            {run.campus?.name ?? 'Any campus'}
            {run.building?.name ? ` · ${run.building.name}` : ''}
          </dd>
        </div>
        <div>
          <dt>Leader</dt>
          <dd>{run.leader.displayName}</dd>
        </div>
        <div>
          <dt>Run stock</dt>
          <dd>{run.stockBalance}</dd>
        </div>
      </dl>
      <p className="participant-summary">
        <strong>{run.participantCount} participating:</strong>{' '}
        {run.participants.join(', ') || 'No active participants'}
      </p>
      {run.notes ? <p>{run.notes}</p> : null}
      {join.error || update.error || close.error ? (
        <ErrorNotice error={join.error ?? update.error ?? close.error} />
      ) : null}
      {!['completed', 'cancelled'].includes(run.status) ? (
        <div className="button-row">
          {!joined ? (
            <button
              className="button button-primary"
              type="button"
              onClick={() => join.mutate()}
              disabled={join.isPending}
            >
              Join run
            </button>
          ) : (
            <StatusPill tone="success">You joined</StatusPill>
          )}
          {canManage && run.status === 'planned' ? (
            <button className="button" type="button" onClick={() => update.mutate('active')}>
              Start run
            </button>
          ) : null}
          {canManage && run.status === 'active' ? (
            <button className="button" type="button" onClick={() => update.mutate('paused')}>
              Pause
            </button>
          ) : null}
          {canManage && run.status === 'paused' ? (
            <button className="button" type="button" onClick={() => update.mutate('active')}>
              Resume
            </button>
          ) : null}
          {canManage ? (
            <button
              className="button button-danger-text"
              type="button"
              onClick={() => setShowClose((current) => !current)}
            >
              Close run
            </button>
          ) : null}
        </div>
      ) : null}
      {showClose ? (
        <form
          className="inline-form close-run-form"
          onSubmit={(event) => {
            event.preventDefault();
            close.mutate('completed');
          }}
        >
          <label>
            Flyers being returned
            <input
              type="number"
              min={0}
              max={returnableBalance}
              value={returnedQuantity}
              onChange={(event) => setReturnedQuantity(Number(event.target.value))}
              required
            />
          </label>
          {run.stockBalance < 0 ? (
            <p>
              This run is <strong>{Math.abs(run.stockBalance)} flyers below zero</strong>. Return
              quantity is fixed at 0; close the run, then reconcile the deficit in inventory.
            </p>
          ) : (
            <p>
              Current run balance: <strong>{run.stockBalance}</strong>. Any unreturned balance stays
              visible as a reconciliation warning.
            </p>
          )}
          <button className="button button-primary" disabled={close.isPending}>
            {close.isPending ? 'Closing…' : 'Return stock and close'}
          </button>
          <button
            className="button button-danger-text"
            type="button"
            disabled={close.isPending}
            onClick={() => close.mutate('cancelled')}
          >
            Return stock and cancel run
          </button>
        </form>
      ) : null}
    </article>
  );
}

export function RunsPage({
  campaign,
  me,
  role,
}: {
  campaign: Campaign;
  me: Me;
  role: CampaignRole;
}) {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [campusId, setCampusId] = useState('');
  const coordinator = role === 'admin' || role === 'coordinator';
  const runs = useQuery({
    queryKey: ['runs', campaign.id],
    queryFn: () => apiClient.runs(campaign.id),
  });
  const waves = useQuery({
    queryKey: ['waves', campaign.id],
    queryFn: () => apiClient.waves(campaign.id),
  });
  const dashboard = useQuery({
    queryKey: ['dashboard', campaign.id],
    queryFn: () => apiClient.dashboard(campaign.id),
  });
  const create = useMutation({
    mutationFn: (input: Record<string, unknown>) => apiClient.createRun(input),
    onSuccess: async () => {
      setShowCreate(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['runs'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
      ]);
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const startsAt = zonedDateTimeToIso(formString(form, 'startsAt'), campaign.timezone);
    create.mutate({
      campaignId: campaign.id,
      waveId: form.get('waveId'),
      name: form.get('name'),
      startsAt,
      campusId: form.get('campusId') || undefined,
      buildingId: form.get('buildingId') || undefined,
      notes: form.get('notes') || undefined,
      status: 'planned',
    });
  };
  const campuses = dashboard.data?.data.campuses ?? [];
  const buildings = (dashboard.data?.data.buildings ?? []).filter(
    (building) => !campusId || stringValue(building.campus_id) === campusId,
  );
  const openRuns = runs.data?.data.filter((run) =>
    ['planned', 'active', 'paused'].includes(run.status),
  );
  const recentRuns = runs.data?.data.filter((run) =>
    ['completed', 'cancelled'].includes(run.status),
  );

  return (
    <div className="page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Groups and shifts</p>
          <h1>Distribution runs</h1>
        </div>
        {coordinator ? (
          <button
            className="button button-primary"
            type="button"
            onClick={() => setShowCreate((current) => !current)}
          >
            {showCreate ? 'Cancel' : 'Create run'}
          </button>
        ) : null}
      </header>
      <p className="page-intro">
        Join the run you are working with. When a group splits, choose the exact participants while
        recording each room.
      </p>
      {showCreate ? (
        <Section title="Plan a run">
          {create.error ? <ErrorNotice error={create.error} /> : null}
          <form className="form-grid" onSubmit={submit}>
            <label className="field-wide">
              Run name
              <input name="name" placeholder="Kelburn morning group" required />
            </label>
            <label>
              Wave
              <select name="waveId" required>
                {waves.data?.data.map((wave) => (
                  <option value={wave.id} key={wave.id}>
                    {wave.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Starts
              <input
                name="startsAt"
                type="datetime-local"
                defaultValue={nextQuarterHourValue(campaign.timezone)}
                required
              />
            </label>
            <label>
              Campus focus
              <select
                name="campusId"
                value={campusId}
                onChange={(event) => setCampusId(event.target.value)}
              >
                <option value="">Any campus</option>
                {campuses.map((campus) => (
                  <option value={stringValue(campus.id)} key={stringValue(campus.id)}>
                    {stringValue(campus.name)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Building focus
              <select name="buildingId">
                <option value="">Any building</option>
                {buildings.map((building) => (
                  <option value={stringValue(building.id)} key={stringValue(building.id)}>
                    {stringValue(building.name)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-wide">
              Notes <span className="optional">(optional)</span>
              <textarea name="notes" rows={2} />
            </label>
            <button className="button button-primary" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create run'}
            </button>
          </form>
        </Section>
      ) : null}
      {runs.isLoading ? <Loading label="Loading runs…" /> : null}
      {runs.error ? <ErrorNotice error={runs.error} /> : null}
      <Section title="Open runs">
        {openRuns?.length ? (
          <div className="card-grid">
            {openRuns.map((run) => (
              <RunCard run={run} me={me} role={role} key={run.id} />
            ))}
          </div>
        ) : (
          <EmptyState title="No open runs">
            Coordinators can create the next group shift here.
          </EmptyState>
        )}
      </Section>
      {recentRuns?.length ? (
        <Section title="Recent closed runs">
          <div className="card-grid">
            {recentRuns.slice(0, 8).map((run) => (
              <RunCard run={run} me={me} role={role} key={run.id} />
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}
